/**
 * LawDocPanel - Ask UI redesign Phase A (PR-4 part 2). Right-side legal-doc
 * tabs panel for /chat.
 *
 * Composition (REUSE, do NOT fork):
 *   - Tab 0 "Nguồn (N)": the grounded source list, delegated to the existing
 *     CitationSourcesPanel (extended with an optional onOpenDoc so a click
 *     opens the full text in-panel instead of navigating away to /van-ban/:id).
 *   - Per-doc tab: the full text, delegated to the existing RawHtmlViewer
 *     (R2 raw-html -> DOMPurify -> iframe srcdoc, with jumpToAnchor handle).
 *
 * Tab model:
 *   - The "Nguồn" list tab is the fixed first tab (id = NGUON_TAB). It is the
 *     default-active tab on mount; only it is open initially.
 *   - Opening a doc (handleOpenDoc) appends its doc_id to `openDocIds` (deduped)
 *     and activates it. Cap = TOP_K (6); opening a 7th evicts the OLDEST opened
 *     doc tab (FIFO) so the strip never exceeds 6 doc tabs. (No logging.)
 *   - Closing a doc tab removes it; if it was active, fall back to the previous
 *     tab in the strip (or the Nguồn list tab).
 *   - The tab strip scrolls horizontally (overflow-x: auto) when it overflows.
 *
 * Highlight LIMITATION (spec 3.4, Phase A): grounding is DOC-LEVEL (no provision
 * id), so a precise Dieu/Khoan jump + highlight is a fast-follow. This part
 * ships the SAFE version: open the full text from the TOP (clear orientation -
 * which law + eff status - plus a scrollable body) and let the reader scroll to
 * the relevant part. The earlier "auto-jump to the first Dieu" was REMOVED: with
 * no provision id it dropped the reader mid-document on unrelated content
 * (founder UAT 2026-06-15 - "thua ke" query opened Bo luat Dan su on Chuong III
 * / Dieu 16). Precise provision jump + snippet highlight stay DEFERRED.
 *   // TODO(PR-4): provision-level jump + snippet highlight - deferred (spec 3.4)
 *
 * Styling: co-located <style> (design tokens only) per CitationSourcesPanel
 * convention - keeps the lightweight route from pulling chat.css. Desktop dock
 * width 380px (matches the 360px CitationSourcesPanel rail). Tabs + close
 * buttons are keyboard-accessible (role=tablist/tab/tabpanel, aria-selected,
 * close <button> with aria-label "Đóng tab").
 *
 * VN copy: full diacritics per F-FE-CHAT AC-21. No new npm deps.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import type { GroundedSourceDoc } from '../../lib/chat-api';
import { CitationSourcesPanel } from './CitationSourcesPanel';
import { RawHtmlViewer } from '../corpus/RawHtmlViewer';

/** Max simultaneously-open doc tabs (TOP_K). 7th open evicts the oldest. */
const TOP_K = 6;

/** Sentinel tab id for the always-present "Nguồn" source-list tab. */
const NGUON_TAB = '__nguon__';

const COPY = {
  vi: {
    sources: (n: number) => `Nguồn (${n})`,
    open: 'Mở toàn văn',
    closeTab: 'Đóng tab',
    panelLabel: 'Văn bản pháp luật',
    sourcesTabLabel: 'Danh sách nguồn trích dẫn',
    expand: 'Mở rộng khung văn bản',
    restore: 'Thu gọn khung văn bản',
    minimize: 'Thu nhỏ khung văn bản',
    restorePanel: 'Khôi phục khung văn bản',
  },
  en: {
    sources: (n: number) => `Sources (${n})`,
    open: 'Open full text',
    closeTab: 'Close tab',
    panelLabel: 'Legal documents',
    sourcesTabLabel: 'Cited sources list',
    expand: 'Expand document panel',
    restore: 'Shrink document panel',
    minimize: 'Minimize document panel',
    restorePanel: 'Restore document panel',
  },
} as const;

/** Panel width state (desktop >=1200px). Mobile = bottom-sheet, ignores this. */
export type PanelSize = 'normal' | 'expanded' | 'minimized';

/** Width action a control can request. 'restore' = un-minimize to prev size. */
export type PanelSizeAction = PanelSize | 'restore';

/**
 * SINGLE source of truth for the panel width per state. Used by BOTH this panel
 * (the fixed dock's own width) AND chat.tsx (the .chat-layout grid 3rd column,
 * so the chat shrinks in lock-step). Keeping it one function prevents the
 * 380-vs-2x drift the old hardcoded width risked.
 * Returned string is fed to a CSS custom property; only the desktop media query
 * consumes it (mobile keeps the full-width bottom sheet).
 */
export function panelWidthFor(size: PanelSize): string {
  if (size === 'minimized') return '44px';
  if (size === 'expanded') return 'clamp(560px, 48vw, 880px)';
  return '380px';
}

const PANEL_CSS = `
.asklaw-lawdoc {
  --ald-bg: var(--asklaw-panel-bg, #ffffff);
  --ald-elev: var(--asklaw-paper-elev, #ffffff);
  --ald-border: var(--asklaw-border, #e4e7ee);
  --ald-accent: var(--asklaw-accent, #f38020);
  --ald-muted: var(--asklaw-muted, #6b7180);
  --ald-radius: var(--radius-md, 10px);
  font-family: Inter, system-ui, sans-serif;
  box-sizing: border-box;
}
.asklaw-lawdoc *, .asklaw-lawdoc *::before, .asklaw-lawdoc *::after { box-sizing: border-box; }

/* Mobile-first: a full-width bottom sheet for the active full-text doc tab.
   The "Nguồn" list tab delegates to CitationSourcesPanel's own bottom-sheet,
   so on mobile this dock only needs to host the tab strip + a doc tabpanel. */
.asklaw-lawdoc {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 41;
  display: flex;
  flex-direction: column;
  max-height: 82vh;
  background: var(--ald-bg);
  border-top: 2px solid var(--ald-accent);
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -8px 24px -8px rgba(15, 23, 42, 0.25);
}
/* Issue 1 followup: CitationSourcesPanel is now EMBEDDED (renders in normal
   flow inside this panel's body, NOT self-docking fixed). So even when only the
   Nguồn list tab is open, THIS panel is the single docked surface that hosts the
   source cards - it must stay visible + interactive. The old --list-only mode
   (transparent + pointer-events:none, which let the self-docking panel own the
   rail) is retired; --list-only now only tweaks the bottom-sheet max-height so
   a long source list can scroll on mobile. */
.asklaw-lawdoc--list-only {
  max-height: 82vh;
}

/* Header = tab strip (scrolls horizontally) + a fixed control cluster
   (expand/restore + minimize). Bounding the strip in a flex row with controls
   pinned right fixes the cramped/overlapping tabs at the rounded top edge
   (founder UAT 2026-06-15). */
.asklaw-lawdoc__header {
  display: flex;
  align-items: stretch;
  flex: 0 0 auto;
  background: var(--ald-elev);
  border-bottom: 1px solid var(--ald-border);
  border-radius: 16px 16px 0 0;
}
.asklaw-lawdoc__tabstrip {
  display: flex;
  align-items: stretch;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  padding: 6px 8px;
  -webkit-overflow-scrolling: touch;
}
.asklaw-lawdoc__controls {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  padding: 6px 8px 6px 4px;
}
.asklaw-lawdoc__ctrl {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid var(--ald-border);
  border-radius: var(--ald-radius);
  background: transparent;
  color: var(--ald-muted);
  cursor: pointer;
  line-height: 1;
}
.asklaw-lawdoc__ctrl:hover { background: color-mix(in srgb, var(--ald-muted) 18%, transparent); }
.asklaw-lawdoc__ctrl:focus-visible {
  outline: 2px solid var(--ald-accent);
  outline-offset: 1px;
}
/* Resize/minimize controls are a desktop affordance (mobile = full-width
   bottom sheet, nothing to resize). */
@media (max-width: 1199.98px) {
  .asklaw-lawdoc__controls { display: none; }
}
.asklaw-lawdoc__tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  max-width: 200px;
  padding: 7px 10px;
  border: 1px solid var(--ald-border);
  border-radius: var(--ald-radius);
  background: transparent;
  color: var(--ald-muted);
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  min-height: 34px;
}
/* Wrapper holding a doc tab (role=tab, label-only) + its sibling close button.
   The Nguon list tab is a standalone .asklaw-lawdoc__tab with no wrapper. The
   wrapper mirrors the tab's selected chrome so the close button reads as part
   of the same visual pill while staying a DOM/ARIA sibling of the tab. */
.asklaw-lawdoc__tabwrap {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  border-radius: var(--ald-radius);
}
/* Doc tabs live inside a wrapper: drop their right edge so the close button
   tucks against the label without a double border seam. */
.asklaw-lawdoc__tabwrap .asklaw-lawdoc__tab {
  border-right: none;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.asklaw-lawdoc__tab-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.asklaw-lawdoc__tab[aria-selected="true"] {
  color: var(--ald-accent);
  border-color: var(--ald-accent);
  background: color-mix(in srgb, var(--ald-accent) 8%, transparent);
}
/* Keep the close button visually matched to its selected sibling tab. */
.asklaw-lawdoc__tabwrap:has(.asklaw-lawdoc__tab[aria-selected="true"]) .asklaw-lawdoc__close {
  color: var(--ald-accent);
  border-color: var(--ald-accent);
  background: color-mix(in srgb, var(--ald-accent) 8%, transparent);
}
.asklaw-lawdoc__tab:focus-visible {
  outline: 2px solid var(--ald-accent);
  outline-offset: 1px;
}
.asklaw-lawdoc__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 26px;
  align-self: stretch;
  min-height: 34px;
  padding: 0;
  border: 1px solid var(--ald-border);
  border-left: none;
  border-top-right-radius: var(--ald-radius);
  border-bottom-right-radius: var(--ald-radius);
  background: transparent;
  color: var(--ald-muted);
  cursor: pointer;
  line-height: 1;
}
.asklaw-lawdoc__close:hover { background: color-mix(in srgb, var(--ald-muted) 18%, transparent); }
.asklaw-lawdoc__close:focus-visible {
  outline: 2px solid var(--ald-accent);
  outline-offset: 1px;
}
.asklaw-lawdoc__panel {
  flex: 1 1 auto;
  /* min-height:0 lets this flex child shrink below its content size so its OWN
     overflow-y can scroll. Without it the panel grows to the (tall, auto-height)
     iframe and the fixed + overflow:hidden section just clips it -> no scroll
     (founder UAT 2026-06-15). Same idiom as .chat-layout__main in chat.css. */
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 12px 14px 20px;
  background: var(--ald-bg);
}
.asklaw-lawdoc__panel--list { padding: 0; background: transparent; }

/* Minimized rail: panel collapses to a thin vertical bar docked at the right;
   click restores the previous size. Header + content tabpanels stay MOUNTED but
   hidden (header via this rule, panels via inline display) so the iframe + its
   scroll position survive a minimize -> restore round trip (no refetch). */
.asklaw-lawdoc__rail { display: none; }
.asklaw-lawdoc--minimized .asklaw-lawdoc__header { display: none; }
.asklaw-lawdoc--minimized .asklaw-lawdoc__rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  flex: 1 1 auto;
  width: 100%;
  padding: 14px 4px;
  border: none;
  background: transparent;
  color: var(--ald-accent);
  cursor: pointer;
  font-family: inherit;
}
.asklaw-lawdoc--minimized .asklaw-lawdoc__rail:focus-visible {
  outline: 2px solid var(--ald-accent);
  outline-offset: -2px;
  border-radius: 12px;
}
.asklaw-lawdoc__rail-label {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  color: var(--ald-muted);
  letter-spacing: 0.02em;
}

/* 1200px = 72 NavRail + 280 history + ~760 main + 380 panel - khop phantom
   column .chat-layout--has-panel trong chat.css (spec 2026-06-12 muc 3.4).
   top: 0 vi global Header da route-gate off /chat (NavRail thay the). */
@media (min-width: 1200px) {
  /* Phase 1B-2 inset card: panel canh voi cac the (shell padding 10px) - right/
     top/bottom 10px + bo goc 18px + bong do, thay vi ban sat canh viewport. Khop
     phantom column 380px cua .chat-layout--has-panel (chat.css). */
  .asklaw-lawdoc {
    left: auto;
    right: 10px;
    top: 10px;
    bottom: 10px;
    /* Width is state-driven (normal 380 / expanded clamp / minimized 44) via a
       CSS var fed from panelWidthFor() - the SAME function chat.tsx uses for the
       .chat-layout grid 3rd column, so the chat shrinks in lock-step (no drift). */
    width: var(--ald-w, 380px);
    max-height: none;
    border: 1px solid var(--ald-border);
    border-left: 2px solid var(--ald-accent);
    border-radius: 18px;
    box-shadow: var(--asklaw-glass-shadow, -8px 0 24px -12px rgba(15, 23, 42, 0.2));
    overflow: hidden;
    transition: width 0.2s ease;
  }
  .asklaw-lawdoc--list-only {
    max-height: none;
  }
  .asklaw-lawdoc__header { border-radius: 18px 18px 0 0; }
}

@media (prefers-reduced-motion: reduce) {
  .asklaw-lawdoc, .asklaw-lawdoc * { transition: none !important; }
}

/* Liquid Glass (spec 2026-06-12 muc 4.2): dock panel = blur surface 4/6. */
.asklaw-lawdoc {
  background: var(--asklaw-glass-bg-strong, var(--ald-bg));
  -webkit-backdrop-filter: blur(var(--asklaw-glass-blur, 18px)) saturate(var(--asklaw-glass-saturate, 170%));
  backdrop-filter: blur(var(--asklaw-glass-blur, 18px)) saturate(var(--asklaw-glass-saturate, 170%));
}
`;

function CloseIcon(): ReactElement {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

/** Generic 16px control glyph wrapper (expand / restore / minimize / rail). */
function CtrlGlyph({ d }: { d: string }): ReactElement {
  return (
    <svg
      width={15}
      height={15}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

// Expand: arrows pushing outward (maximize). Restore: arrows pulling inward.
// Minimize: double chevron pointing right (collapse to the right rail).
// Rail: double chevron pointing left (pull the panel back open).
const ICON_EXPAND = 'M15 3h6v6M21 3l-7 7M9 21H3v-6M3 21l7-7';
const ICON_RESTORE = 'M20 10h-6V4M14 10l7-7M4 14h6v6M10 14l-7 7';
const ICON_MINIMIZE = 'M13 6l6 6-6 6M5 6l6 6-6 6';
const ICON_RAIL = 'M11 6l-6 6 6 6M19 6l-6 6 6 6';

/** Short, human label for a doc tab: prefer doc_num, fall back to title. */
function tabLabelFor(doc: GroundedSourceDoc | undefined, docId: string): string {
  if (!doc) return docId;
  if (doc.doc_num) return doc.doc_num;
  if (doc.title) return doc.title.length > 28 ? `${doc.title.slice(0, 27)}…` : doc.title;
  return docId;
}

interface LawDocPanelProps {
  /** From the latest grounded answer's `sources` SSE event. */
  docs: GroundedSourceDoc[];
  lang: 'vi' | 'en';
  /** Optional per-doc snippet to highlight (default: doc.snippet). Reserved for
   *  the deferred snippet-highlight fast-follow; currently informational only. */
  activeSnippetByDocId?: Record<string, string>;
  /** Desktop width state, owned by chat.tsx (single source of truth so the
   *  .chat-layout grid shrinks in lock-step). Defaults to 'normal'. */
  size?: PanelSize;
  /** Request a width change. 'restore' = un-minimize to the previous size.
   *  chat.tsx applies it + tracks prev-size. */
  onSizeChange?: (action: PanelSizeAction) => void;
}

/**
 * Full-text body for one doc tab. Mounts RawHtmlViewer (R2 raw-html ->
 * DOMPurify -> auto-height iframe) and renders the WHOLE document from the top.
 *
 * Open position = document TOP (no auto-jump). Doc-level grounding has no
 * provision id, so the previous "jump to the first Dieu" was a guess that
 * dropped the reader mid-document with no orientation (founder UAT 2026-06-15).
 * A PRECISE jump to the relevant Dieu is the deferred provision-grounding
 * fast-follow (spec 3.4 limitation). The scroll container is the role=tabpanel
 * wrapper below (it carries .asklaw-lawdoc__panel), so RawHtmlViewer renders
 * directly here with no extra wrapper.
 */
function DocTabPanel({ docId }: { docId: string }): ReactElement {
  return <RawHtmlViewer docId={docId} />;
}

export function LawDocPanel({
  docs,
  lang,
  size = 'normal',
  onSizeChange,
}: LawDocPanelProps): ReactElement | null {
  const t = COPY[lang];

  // Open doc tabs in FIFO order. The "Nguồn" list tab is implicit (always tab 0).
  const [openDocIds, setOpenDocIds] = useState<string[]>([]);
  // Active tab: NGUON_TAB or a doc_id. Starts on the source-list tab.
  const [activeTab, setActiveTab] = useState<string>(NGUON_TAB);
  // Tablist ref: after closing the active tab the close button unmounts and
  // focus falls to <body>; we move it back to the newly-active tab element.
  const tablistRef = useRef<HTMLDivElement>(null);

  // New answer (docs identity change) -> reset to a clean source-list view so
  // stale doc tabs from a previous question do not linger, and collapse the
  // panel back to its normal width (the prior answer may have left it expanded).
  useEffect(() => {
    setOpenDocIds([]);
    setActiveTab(NGUON_TAB);
    onSizeChange?.('normal');
  }, [docs, onSizeChange]);

  const docById = useMemo(() => {
    const m = new Map<string, GroundedSourceDoc>();
    for (const d of docs) m.set(d.doc_id, d);
    return m;
  }, [docs]);

  const handleOpenDoc = useCallback(
    (doc: GroundedSourceDoc) => {
      setOpenDocIds((prev) => {
        if (prev.includes(doc.doc_id)) return prev; // already open, just activate
        const next = [...prev, doc.doc_id];
        // Cap at TOP_K: evict the OLDEST (FIFO) when a 7th is opened.
        return next.length > TOP_K ? next.slice(next.length - TOP_K) : next;
      });
      setActiveTab(doc.doc_id);
      // Auto-expand on opening a full text (founder 2026-06-15) - but never yank
      // a deliberately minimized panel back open.
      if (size !== 'minimized') onSizeChange?.('expanded');
    },
    [size, onSizeChange],
  );

  const handleCloseTab = useCallback(
    (docId: string) => {
      const idx = openDocIds.indexOf(docId);
      if (idx === -1) return;
      // Compute BOTH next values in the handler body (no setState nested inside
      // another setter's updater), then apply them sequentially.
      const nextOpen = openDocIds.filter((id) => id !== docId);
      // If we closed the active tab, fall back to the previous strip tab (the
      // one before it), else the Nguồn list tab. (Same fallback logic as before.)
      let nextActive = activeTab;
      if (activeTab === docId) {
        const fallback = nextOpen[idx - 1] ?? nextOpen[idx] ?? nextOpen[nextOpen.length - 1];
        nextActive = fallback ?? NGUON_TAB;
      }
      setOpenDocIds(nextOpen);
      setActiveTab(nextActive);

      // Focus management: the close button just unmounted, so focus has fallen
      // to <body>. Move it to the newly-active tab so keyboard users keep
      // context. Defer a tick so React has committed the new aria-selected,
      // then focus whichever role=tab is now selected (null-safe / SSR-safe).
      if (activeTab === docId && typeof window !== 'undefined') {
        window.setTimeout(() => {
          const el = tablistRef.current?.querySelector<HTMLElement>(
            '[role="tab"][aria-selected="true"]',
          );
          el?.focus();
        }, 0);
      }
    },
    [openDocIds, activeTab],
  );

  // Empty / absent -> hide gracefully (matches CitationSourcesPanel).
  if (!docs || docs.length === 0) return null;

  const listOnly = openDocIds.length === 0;
  const minimized = size === 'minimized';
  const sectionClass = [
    'asklaw-lawdoc',
    listOnly ? 'asklaw-lawdoc--list-only' : '',
    size === 'expanded' ? 'asklaw-lawdoc--expanded' : '',
    minimized ? 'asklaw-lawdoc--minimized' : '',
  ]
    .filter(Boolean)
    .join(' ');
  // Desktop width var: single source of truth = panelWidthFor (chat.tsx feeds
  // the SAME value into the .chat-layout grid 3rd column). Only the desktop
  // media query consumes --ald-w; mobile keeps the full-width bottom sheet.
  const sectionStyle = { '--ald-w': panelWidthFor(size) } as CSSProperties;

  return (
    <section className={sectionClass} aria-label={t.panelLabel} style={sectionStyle}>
      <style>{PANEL_CSS}</style>

      {/* Header: tab strip + resize/minimize controls. Bounding the strip in a
          flex row with controls pinned right keeps the tabs from cramming at the
          rounded top edge (founder UAT 2026-06-15). */}
      <div className="asklaw-lawdoc__header">
        <div
          ref={tablistRef}
          className="asklaw-lawdoc__tabstrip"
          role="tablist"
          aria-label={t.panelLabel}
        >
          {/* Tab 0: always-present source list. */}
          <button
            type="button"
            role="tab"
            id={`lawdoc-tab-${NGUON_TAB}`}
            aria-selected={activeTab === NGUON_TAB}
            aria-controls={`lawdoc-panel-${NGUON_TAB}`}
            aria-label={t.sourcesTabLabel}
            className="asklaw-lawdoc__tab"
            onClick={() => setActiveTab(NGUON_TAB)}
          >
            <span className="asklaw-lawdoc__tab-label">{t.sources(docs.length)}</span>
          </button>

          {/* Per-doc full-text tabs, in FIFO order. The close control is a
              SIBLING of the role=tab element (not nested inside it) so the tab
              keeps a clean ARIA structure + the close button stays reachable via
              its own tab stop. Both live inside a wrapper that carries the visual
              tab chrome. */}
          {openDocIds.map((docId) => {
            const doc = docById.get(docId);
            const label = tabLabelFor(doc, docId);
            const selected = activeTab === docId;
            return (
              <div key={docId} className="asklaw-lawdoc__tabwrap">
                <span
                  role="tab"
                  id={`lawdoc-tab-${docId}`}
                  aria-selected={selected}
                  aria-controls={`lawdoc-panel-${docId}`}
                  className="asklaw-lawdoc__tab"
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActiveTab(docId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setActiveTab(docId);
                    }
                  }}
                >
                  <span className="asklaw-lawdoc__tab-label">{label}</span>
                </span>
                <button
                  type="button"
                  className="asklaw-lawdoc__close"
                  aria-label={`${t.closeTab}: ${label}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseTab(docId);
                  }}
                >
                  <CloseIcon />
                </button>
              </div>
            );
          })}
        </div>

        {/* Resize/minimize controls (desktop only - CSS hides on mobile). */}
        <div className="asklaw-lawdoc__controls">
          <button
            type="button"
            className="asklaw-lawdoc__ctrl"
            aria-label={size === 'expanded' ? t.restore : t.expand}
            aria-expanded={size === 'expanded'}
            onClick={() => onSizeChange?.(size === 'expanded' ? 'normal' : 'expanded')}
          >
            <CtrlGlyph d={size === 'expanded' ? ICON_RESTORE : ICON_EXPAND} />
          </button>
          <button
            type="button"
            className="asklaw-lawdoc__ctrl"
            aria-label={t.minimize}
            onClick={() => onSizeChange?.('minimized')}
          >
            <CtrlGlyph d={ICON_MINIMIZE} />
          </button>
        </div>
      </div>

      {/* Minimized rail: only visible (via CSS) when --minimized. Clicking it
          restores the previous size. Rendered always so the iframe tabpanels
          below stay mounted (no refetch on restore). */}
      <button
        type="button"
        className="asklaw-lawdoc__rail"
        aria-label={t.restorePanel}
        onClick={() => onSizeChange?.('restore')}
        tabIndex={minimized ? 0 : -1}
        aria-hidden={!minimized}
      >
        <CtrlGlyph d={ICON_RAIL} />
        <span className="asklaw-lawdoc__rail-label">{t.sources(docs.length)}</span>
      </button>

      {/* Tabpanels: ALL open doc tabpanels stay MOUNTED at once (cap = TOP_K);
          inactive ones are hidden via display:none + `hidden`/aria-hidden, NOT
          unmounted. This preserves each DocTabPanel's RawHtmlViewer instance
          (its iframe + fetched/sanitized HTML + scroll position) across tab
          switches - switching A->B->A no longer refetches or re-sanitizes A.
          When minimized, ALL panels are display:none (the rail takes over) but
          stay mounted so restore does not refetch. */}

      {/* Source-list tab. Remounting the list is cheap, but keeping it shown/
          hidden by display (not unmounted) is consistent with the doc tabs. */}
      <div
        role="tabpanel"
        id={`lawdoc-panel-${NGUON_TAB}`}
        aria-labelledby={`lawdoc-tab-${NGUON_TAB}`}
        className="asklaw-lawdoc__panel asklaw-lawdoc__panel--list"
        hidden={activeTab !== NGUON_TAB || minimized}
        aria-hidden={activeTab !== NGUON_TAB || minimized}
        style={{ display: activeTab === NGUON_TAB && !minimized ? 'block' : 'none' }}
      >
        {/* Delegate the source list; onOpenDoc switches its action to the
            in-panel "Mở toàn văn" button (opens a doc tab here). */}
        <CitationSourcesPanel docs={docs} onOpenDoc={handleOpenDoc} />
      </div>

      {/* One MOUNTED tabpanel per OPENED doc (at most TOP_K, only docs the user
          actually opened -> at most as many iframes as tabs opened). Keyed by
          the stable docId (NOT activeTab) so React preserves each instance. */}
      {openDocIds.map((docId) => {
        const active = activeTab === docId && !minimized;
        return (
          <div
            key={docId}
            role="tabpanel"
            id={`lawdoc-panel-${docId}`}
            aria-labelledby={`lawdoc-tab-${docId}`}
            className="asklaw-lawdoc__panel"
            hidden={!active}
            aria-hidden={!active}
            style={{ display: active ? 'block' : 'none' }}
          >
            <DocTabPanel docId={docId} />
          </div>
        );
      })}
    </section>
  );
}

export default LawDocPanel;
